import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  UsersIcon,
  EyeIcon,
  PhoneIcon,
  EnvelopeIcon,
  MapPinIcon,
  StarIcon,
  BriefcaseIcon,
  CalendarIcon
} from '@heroicons/react/24/outline';
import { API_BASE } from '../lib/config';
import { ShieldCheckIcon, LinkIcon } from '@heroicons/react/24/outline';

// API_BASE already points to "/api"
const API = API_BASE;

const JobSeekerManagePage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [jobSeekers, setJobSeekers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedJobSeeker, setSelectedJobSeeker] = useState(null);
  const [showModal, setShowModal] = useState(false);
  // Stripe Connect status
  const [connectStatus, setConnectStatus] = useState({ exists: false, charges_enabled: false, payouts_enabled: false, details_submitted: false, account_id: null });
  const [connectLoading, setConnectLoading] = useState(false);
  const [creatingLink, setCreatingLink] = useState(false);

  useEffect(() => {
    if (!user) {
      toast.error('ログインが必要です');
      navigate('/login');
      return;
    }
    // 管理運営者だけでなく、求職者ユーザー自身の学習/確認用途としても閲覧可能にする
    // 事業者ユーザーのみトップへ戻す（必要なら要件に合わせて調整）
    if (user.user_type === 'employer') {
      toast.error('このページは求職者/管理者向けです');
      navigate('/');
      return;
    }

    fetchJobSeekers();
    // 求職者自身が閲覧する場合、Connect状態を取得
    if (user.user_type === 'job_seeker') {
      void fetchConnectStatus();
    }
  }, [user, navigate]);

  const fetchJobSeekers = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/admin/users?type=job_seeker`);

      // Adapt API response to component state structure if needed
      // API returns snake_case keys like is_verified, created_at
      // Component expects: id, name, email, phone, location, bio, skills[], rating, completed_jobs...
      // The API returns basic info. Some columns might be empty or need adjustment.
      const adapted = res.data.map(u => ({
        ...u,
        phone: u.phone || '-',
        location: u.location || '-',
        skills: u.skills || [],
        rating: u.rating || 0,
        completed_jobs: u.completed_jobs_count || 0,
        last_active: u.last_active_at
      }));

      setJobSeekers(adapted);
    } catch (error) {
      console.error(error);
      toast.error('求職者データの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const fetchConnectStatus = async () => {
    try {
      setConnectLoading(true);
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/connect/accounts/me`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setConnectStatus({
          exists: !!data.exists,
          account_id: data.account_id || null,
          charges_enabled: !!data.charges_enabled,
          payouts_enabled: !!data.payouts_enabled,
          details_submitted: !!data.details_submitted
        });
      }
    } catch (e) {
      // noop
    } finally {
      setConnectLoading(false);
    }
  };

  const startOnboarding = async () => {
    try {
      setCreatingLink(true);
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/connect/account-links`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({})
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.detail || 'オンボーディング開始に失敗しました');
      }
      const { url } = await res.json();
      window.location.href = url;
    } catch (e) {
      toast.error(e.message || 'オンボーディング開始に失敗しました');
    } finally {
      setCreatingLink(false);
    }
  };

  const filteredJobSeekers = jobSeekers.filter(jobSeeker =>
    jobSeeker.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    jobSeeker.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    jobSeeker.location.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('ja-JP', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const renderStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;

    for (let i = 0; i < fullStars; i++) {
      stars.push(<StarIcon key={i} className="w-4 h-4 text-yellow-400 fill-current" />);
    }

    if (hasHalfStar) {
      stars.push(<StarIcon key="half" className="w-4 h-4 text-yellow-400 fill-current opacity-50" />);
    }

    const emptyStars = 5 - Math.ceil(rating);
    for (let i = 0; i < emptyStars; i++) {
      stars.push(<StarIcon key={`empty-${i}`} className="w-4 h-4 text-gray-300" />);
    }

    return stars;
  };

  const viewJobSeekerDetails = (jobSeeker) => {
    setSelectedJobSeeker(jobSeeker);
    setShowModal(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-64 mb-8"></div>
            <div className="grid grid-cols-1 gap-4">
              {[1, 2, 3].map(i => (
                <div key={i} className="bg-white p-6 rounded-lg shadow-sm">
                  <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                  <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">求職者管理</h1>
          <p className="text-gray-600">
            登録済み求職者の情報管理と活動状況の監視
          </p>
        </div>

        {/* 求職者本人向け: 報酬受け取り設定（Stripe Connect） */}
        {user?.user_type === 'job_seeker' && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
            <div className="flex items-start justify-between">
              <div className="flex items-start">
                <div className="p-2 bg-blue-50 rounded-lg mr-4">
                  <ShieldCheckIcon className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">報酬受け取り設定（Stripe Connect）</h3>
                  <p className="text-sm text-gray-600 mt-1">求職者として報酬を受け取るための設定を行います。はじめての方は「設定を開始」から、未完了の方は「再開」からお進みください。</p>
                  <div className="mt-3 flex flex-wrap gap-2 text-xs">
                    <span className={`px-2 py-1 rounded ${connectStatus.details_submitted ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-700'}`}>提出状態: {connectStatus.details_submitted ? '提出済み' : '未提出'}</span>
                    <span className={`px-2 py-1 rounded ${connectStatus.charges_enabled ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-700'}`}>請求可: {connectStatus.charges_enabled ? '有効' : '無効'}</span>
                    <span className={`px-2 py-1 rounded ${connectStatus.payouts_enabled ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-700'}`}>振込可: {connectStatus.payouts_enabled ? '有効' : '無効'}</span>
                    {connectStatus.account_id && (
                      <span className="px-2 py-1 rounded bg-gray-50 text-gray-600">アカウントID: {connectStatus.account_id}</span>
                    )}
                  </div>
                </div>
              </div>
              <div>
                <button
                  onClick={startOnboarding}
                  disabled={creatingLink || connectLoading}
                  className="inline-flex items-center bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  <LinkIcon className="w-4 h-4 mr-2" />
                  {connectStatus.exists ? '設定を再開' : '設定を開始'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Search */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
          <div className="flex items-center space-x-4">
            <div className="flex-1">
              <input
                type="text"
                placeholder="名前、メール、地域で検索..."
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="text-sm text-gray-600">
              {filteredJobSeekers.length}人の求職者
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white p-6 rounded-lg shadow-sm">
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 rounded-lg">
                <UsersIcon className="w-6 h-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">総求職者数</p>
                <p className="text-2xl font-bold text-gray-900">{jobSeekers.length}</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-sm">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 rounded-lg">
                <StarIcon className="w-6 h-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">平均評価</p>
                <p className="text-2xl font-bold text-gray-900">
                  {jobSeekers.length > 0 ?
                    (jobSeekers.reduce((sum, js) => sum + js.rating, 0) / jobSeekers.length).toFixed(1) :
                    '0.0'
                  }
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-sm">
            <div className="flex items-center">
              <div className="p-2 bg-purple-100 rounded-lg">
                <BriefcaseIcon className="w-6 h-6 text-purple-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">総完了件数</p>
                <p className="text-2xl font-bold text-gray-900">
                  {jobSeekers.reduce((sum, js) => sum + js.completed_jobs, 0)}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-sm">
            <div className="flex items-center">
              <div className="p-2 bg-orange-100 rounded-lg">
                <CalendarIcon className="w-6 h-6 text-orange-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">今月新規登録</p>
                <p className="text-2xl font-bold text-gray-900">
                  {jobSeekers.filter(js => {
                    const created = new Date(js.created_at);
                    const now = new Date();
                    return created.getMonth() === now.getMonth() && created.getFullYear() === now.getFullYear();
                  }).length}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Job Seekers List */}
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">求職者一覧</h2>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    求職者情報
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    連絡先
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    評価・実績
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    最終ログイン
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    操作
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredJobSeekers.map((jobSeeker) => (
                  <tr key={jobSeeker.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{jobSeeker.name}</div>
                        <div className="text-sm text-gray-500">{jobSeeker.location}</div>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {jobSeeker.skills.slice(0, 2).map((skill, index) => (
                            <span key={index} className="tag primary text-xs">
                              {skill}
                            </span>
                          ))}
                          {jobSeeker.skills.length > 2 && (
                            <span className="text-xs text-gray-500">
                              +{jobSeeker.skills.length - 2}
                            </span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div className="space-y-1">
                        <div className="flex items-center">
                          <EnvelopeIcon className="w-4 h-4 mr-2" />
                          {jobSeeker.email}
                        </div>
                        <div className="flex items-center">
                          <PhoneIcon className="w-4 h-4 mr-2" />
                          {jobSeeker.phone}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm">
                        <div className="flex items-center mb-1">
                          {renderStars(jobSeeker.rating)}
                          <span className="ml-2 text-gray-600">{jobSeeker.rating}</span>
                        </div>
                        <div className="text-gray-500">
                          完了: {jobSeeker.completed_jobs}件
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(jobSeeker.last_active)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={() => viewJobSeekerDetails(jobSeeker)}
                        className="text-blue-600 hover:text-blue-700 flex items-center mr-3"
                      >
                        <EyeIcon className="w-4 h-4 mr-1" />
                        詳細
                      </button>
                      <button
                        onClick={async () => {
                          if (!window.confirm(`このユーザーを${jobSeeker.status === 'suspended' ? '有効化' : '停止'}しますか？`)) return;
                          try {
                            const newStatus = jobSeeker.status === 'suspended' ? 'active' : 'suspended';
                            await axios.put(`${API}/admin/users/${jobSeeker.id}/status`, { status: newStatus });
                            toast.success(`ユーザーステータスを更新しました`);
                            setJobSeekers(prev => prev.map(u => u.id === jobSeeker.id ? { ...u, status: newStatus } : u));
                          } catch (e) {
                            toast.error('更新に失敗しました');
                          }
                        }}
                        className={`flex items-center ${jobSeeker.status === 'suspended' ? 'text-green-600 hover:text-green-700' : 'text-red-600 hover:text-red-700'}`}
                      >
                        <ShieldCheckIcon className="w-4 h-4 mr-1" />
                        {jobSeeker.status === 'suspended' ? '有効化' : '停止'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Modal */}
        {showModal && selectedJobSeeker && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-900">求職者詳細情報</h3>
                <button
                  onClick={() => setShowModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >

                </button>
              </div>

              <div className="space-y-6">
                {/* Basic Info */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">氏名</label>
                    <p className="mt-1 text-sm text-gray-900">{selectedJobSeeker.name}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">登録日</label>
                    <p className="mt-1 text-sm text-gray-900">{formatDate(selectedJobSeeker.created_at)}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">メールアドレス</label>
                    <p className="mt-1 text-sm text-gray-900">{selectedJobSeeker.email}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">電話番号</label>
                    <p className="mt-1 text-sm text-gray-900">{selectedJobSeeker.phone}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">居住地</label>
                    <p className="mt-1 text-sm text-gray-900">{selectedJobSeeker.location}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">最終ログイン</label>
                    <p className="mt-1 text-sm text-gray-900">{formatDate(selectedJobSeeker.last_active)}</p>
                  </div>
                </div>

                {/* Performance */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">評価</label>
                    <div className="mt-1 flex items-center">
                      {renderStars(selectedJobSeeker.rating)}
                      <span className="ml-2 text-sm text-gray-900">{selectedJobSeeker.rating}/5.0</span>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">完了した仕事</label>
                    <p className="mt-1 text-sm text-gray-900">{selectedJobSeeker.completed_jobs}件</p>
                  </div>
                </div>

                {/* Skills */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">スキル</label>
                  <div className="flex flex-wrap gap-2">
                    {selectedJobSeeker.skills.map((skill, index) => (
                      <span key={index} className="tag primary">
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Bio */}
                <div>
                  <label className="block text-sm font-medium text-gray-700">自己紹介</label>
                  <p className="mt-1 text-sm text-gray-900 whitespace-pre-wrap">
                    {selectedJobSeeker.bio}
                  </p>
                </div>
              </div>

              <div className="mt-6 flex justify-end">
                <button
                  onClick={() => setShowModal(false)}
                  className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors"
                >
                  閉じる
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default JobSeekerManagePage;